import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { isDemoMode } from './lib/demo';
import { demoStore } from './demo/store';
import { profileRepo } from './data/socialRepo';
import { resolveHomeCity } from './data/cityBoot';
import { initCityScope } from './lib/cityScope';
import { syncTraceOwner } from './lib/trace';
import { linkGuestHistory, setAnalyticsUser } from './lib/analytics';
import { setDevLogUser } from './lib/devlog';
import { prefLogRepo } from './data/prefLogRepo';
import { reportError } from './lib/report';
import { signOutEverywhere, type SignOutMode } from './lib/authSignOut';
import { captureProviderTokens } from './lib/appleRevoke';
import type { Profile } from './domain/types';

/** نشستِ قلابی برای حالت دمو - فقط باید truthy باشه، هیچ‌جا محتواش خونده نمیشه. */
const DEMO_SESSION = { user: { id: 'demo-me' } } as unknown as Session;

interface SessionState {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  /**
   * «پروفایل رو **پرسیدیم**» — که با «پروفایل داریم» یکی نیست.
   *
   * بینِ رسیدنِ نشست و رسیدنِ پروفایل یه پنجره‌ی کوتاه هست که توش
   * `profile` خالیه ولی هنوز هیچ‌چی معلوم نیست. هر کدی که از خالی بودنِ
   * پروفایل **نتیجه** می‌گیره (مثلِ «پس هنوز نساختیش») باید تا این پرچم
   * بالا نیاد صبر کنه، وگرنه یه پلک، صفحه‌ی اشتباه نشون می‌ده — نگاه کن به
   * گاردِ `Private` در apps/web/src/app/routes.tsx.
   */
  profileChecked: boolean;
  /** پروفایلِ تازه رو هم برمی‌گردونه تا صداکننده لازم نباشه دوباره بخونتش. */
  refreshProfile: () => Promise<Profile | null>;
  signOut: () => Promise<void>;
  /**
   * خروجِ بعد از حذفِ حساب — مسیرِ جدا، عمداً.
   *
   * فرقش با `signOut` این است که از سرور چیزی نمی‌پرسد: نشستِ سمتِ سرور را
   * خودِ تابعِ حذف پاک کرده (مایگریشن 0046)، پس درخواستِ خروج فقط یک ۴۰۳ و یک
   * راهِ شکستِ اضافه است. نگاه کن به lib/authSignOut.
   */
  signOutAfterDelete: () => Promise<void>;
}

const Ctx = createContext<SessionState>({
  session: null, profile: null, loading: true, profileChecked: false,
  refreshProfile: async () => null,
  signOut: async () => undefined, signOutAfterDelete: async () => undefined,
});
export const useSession = () => useContext(Ctx);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileChecked, setProfileChecked] = useState(false);

  /**
   * پرچم **قبل** از پرسیدن پایین می‌آد و بعدش بالا: تا وقتی جواب نیامده،
   * خالی بودنِ `profile` هیچ معنی‌ای نداره. `finally` یعنی خطای شبکه هم
   * پرچم رو بالا می‌بره — وگرنه کاربر توی یه صفحه‌ی خالیِ ابدی گیر می‌کرد.
   */
  const refreshProfile = async (): Promise<Profile | null> => {
    setProfileChecked(false);
    try {
      const me = await profileRepo.me();
      setProfile(me);
      return me;
    } finally {
      setProfileChecked(true);
    }
  };

  useEffect(() => {
    if (isDemoMode) {
      setSession(DEMO_SESSION);
      setProfile(demoStore.profile.me());
      setProfileChecked(true);
      setLoading(false);
      return;
    }
    /**
     * try/finally عمدیه: قبلاً اگه خوندنِ پروفایل به هر دلیلی می‌ترکید
     * (شبکه، خطای RLS، هرچی)، خطِ `setLoading(false)` هیچ‌وقت اجرا نمی‌شد و
     * `loading` تا ابد true می‌موند. روی سایت نتیجه‌ش یه صفحه‌ی **کاملاً سفید**
     * بدونِ هیچ پیامیه، چون گاردِ مسیرها تا وقتی loading روشنه چیزی رندر
     * نمی‌کنه. حالا هرچی بشه، اپ بالا میاد — فقط بدونِ پروفایل.
     */
    /**
     * توکنِ ارائه‌دهنده (مثلاً Apple) فقط در همین نشستِ تازه همراه است و با
     * اولین رفرش می‌رود — برای پس‌گرفتنش موقعِ حذفِ حساب (WHOSUP-251،
     * `lib/appleRevoke`). روی وب از همین‌جا می‌رسد؛ روی موبایل `setSession`
     * آن را نمی‌آورد و `_layout` خودش از لینک برش می‌دارد.
     */
    const capture = (sess: Session | null) => {
      if (sess) void captureProviderTokens(sess.user?.id, {
        accessToken: sess.provider_token, refreshToken: sess.provider_refresh_token,
      });
    };
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      capture(data.session);
      try {
        if (data.session) await refreshProfile();
      } catch (e) {
        reportError('session.init', e);
      } finally {
        setLoading(false);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_e, sess) => {
      setSession(sess);
      capture(sess);
      if (!sess) { setProfile(null); setProfileChecked(false); return; }
      try {
        await refreshProfile();
      } catch (e) {
        reportError('session.authChange', e);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  /**
   * شناسه‌ی کاربر برای ثبتِ رویداد.
   *
   * این‌جا می‌شینه و نه توی هر اپ، به همون دلیلِ شهر (پایین): جوابش فقط به
   * نشست بستگی داره و دو پیاده‌سازیِ جدا یعنی دو رفتارِ کمی متفاوت.
   *
   * مهم‌تر: سیاستِ نوشتنِ `app_events` می‌گه `user_id` باید دقیقاً برابرِ
   * `auth.uid()` باشه (مایگریشن 0068). اگه این‌جا عقب بمونه، ردیف‌ها بی‌صدا
   * رد می‌شن — نه خطایی، نه داده‌ای.
   */
  useEffect(() => { setAnalyticsUser(session?.user?.id ?? null); }, [session]);
  // سابقه‌ی مهمانِ همین نصب، به‌محضِ اینکه نمایه هست (WHOSUP-308).
  useEffect(() => { if (profile?.id) void linkGuestHistory(profile.id); }, [profile?.id]);

  /**
   * شهرِ ذخیره‌شده‌ی روی همین دستگاه — قبل از هر چیزِ دیگه‌ای، تا اولین
   * فهرست بدونِ صبر برای شبکه پر شه. نگاه کن به lib/cityScope.
   */
  useEffect(() => { void initCityScope(); }, []);

  /**
   * و بعد شهرِ **واقعی**: از پروفایل، یا (روی وبِ بدونِ حساب) از IP.
   *
   * این‌جا می‌شینه نه توی هر اپ، چون جوابش فقط به نشست بستگی داره و دو تا
   * پیاده‌سازیِ جدا یعنی دو تا رفتارِ کمی متفاوت.
   *
   * دو تا شرطِ صبر، هر دو برای یه چیز: تا وقتی **نمی‌دونیم** کی وارد شده،
   * حدس نزن. `loading` یعنی هنوز نشست خونده نشده. شرطِ دوم ظریف‌تره —
   * `onAuthStateChange` نشست رو زودتر از پروفایل می‌ذاره، پس یه لحظه‌ای هست
   * که «وارد شده ولی پروفایلش نرسیده»؛ بدونِ این شرط، اپ همون لحظه شهرِ
   * پیش‌فرض رو می‌ذاشت و یه پلکِ بعد می‌پرید روی شهرِ واقعیِ کاربر.
   */
  useEffect(() => {
    if (loading || (session && !profile)) return;
    resolveHomeCity(profile).catch((e) => reportError('session.city', e));
  }, [loading, session, profile?.id, profile?.cityId]);

  /**
   * ردگیریِ ژست روی خودِ دستگاه ذخیره می‌شه، نه روی حساب. پس تا وقتی نمی‌دونیم
   * کی وارد شده کاری نداریم باهاش، و همین که فهمیدیم، اگه صاحبش نیست خاموشش
   * می‌کنیم. عمداً روی `profile` و نه روی خروج: بیرون رفتن نباید انتخابِ کسی
   * که دوباره وارد می‌شه رو پاک کنه.
   */
  useEffect(() => {
    if (profile) syncTraceOwner(profile.username);
  }, [profile]);

  /**
   * ثبتِ انتخاب‌های کاربر (مایگریشن ۰۱۱۱) — جریانِ یک‌طرفه، فقط موقعِ تغییر.
   *
   * شناسه این‌جا تزریق می‌شود، همان الگوی `setDevLogUser` بالا: بدونش هر
   * ثبت یک `auth.getUser()` اضافه لازم داشت.
   *
   * و `syncLocale`: تنها چیزی که سرِ بوت نوشته می‌شود، و فقط وقتی زبانِ
   * صریحِ روی دستگاه با چیزی که حساب دارد نمی‌خواند. دو شکاف را می‌بندد —
   * کسی که قبل از ساختنِ حساب زبان انتخاب کرده، و همه‌ی حساب‌های امروز که
   * ۰۱۰۹ زبانشان را به null برگرداند. بدونِ این، اعلانِ کسی که زبانش را
   * ماه‌ها پیش انتخاب کرده تا روزی که دوباره دستش بزند انگلیسی می‌ماند.
   */
  useEffect(() => {
    prefLogRepo.setUser(profile?.id ?? null);
    if (!profile) return;
    prefLogRepo.syncLocale(profile).catch((e) => reportError('session.locale', e));
    // «این دستگاه را دیدم» — تنها نوشتنِ سرِ بوت جز زبان. بدونش سوالِ
    // «آخرین مرورگرش کدام بود» جوابی ندارد.
    prefLogRepo.touchDevice().catch((e) => reportError('session.device', e));
  }, [profile?.id, profile?.locale]);

  /**
   * لاگِ فعالیتِ توسعه — فقط برای همون یه حساب (نگاه کن به lib/devlog).
   *
   * `loading` یعنی هنوز نمی‌دونیم نشستی هست یا نه؛ تا اون موقع تصمیمی گرفته
   * نمی‌شه و سطرها توی بافرِ خودِ devlog می‌مونن. کاربرِ واردشده‌ای که
   * پروفایلش هنوز نرسیده هم همین‌طور: بدونِ نامِ کاربری نمی‌شه فهمید صاحبشه
   * یا نه، و حدس زدن یعنی خاموش کردنِ لاگ دقیقاً موقعِ بالا اومدنِ اپ.
   */
  useEffect(() => {
    if (loading) return;
    if (!session) { setDevLogUser(null, null); return; }
    if (profile) setDevLogUser(session.user.id, profile.username);
  }, [loading, session, profile]);

  /**
   * «خروج» توی حالت دمو یعنی بازنشانیِ داده‌ی ساختگی، نه logout واقعی —
   * اینجا /auth اصلاً کار نمی‌کنه چون بک‌اند وصل نیست.
   *
   * و بیرونِ دمو: `signOutEverywhere` خودش هیچ‌وقت throw نمی‌کنه و پاک‌شدنِ
   * نشست از این دستگاه رو تضمین می‌کنه (نگاه کن به lib/authSignOut). خالی
   * کردنِ دستیِ حالت هم عمدیه — اگه درخواستِ سرور شکسته باشه، ممکنه رویدادِ
   * `SIGNED_OUT` اصلاً پخش نشه، و اون‌وقت صفحه‌ی بعدی هنوز فکر می‌کنه یه
   * کاربرِ وارد شده داریم.
   */
  const endSession = async (mode: SignOutMode) => {
    if (isDemoMode) { demoStore.reset(); setProfile(demoStore.profile.me()); return; }
    await signOutEverywhere(mode);
    setSession(null);
    setProfile(null);
  };

  return (
    <Ctx.Provider value={{
      session, profile, loading, profileChecked, refreshProfile,
      signOut: () => endSession('user'),
      signOutAfterDelete: () => endSession('accountGone'),
    }}>
      {children}
    </Ctx.Provider>
  );
}
